/**
 * WPS-150: create-time potential-duplicate claim warning pop-up.
 *
 * Informational only - it never blocks. Shared across every interactive
 * claim-creation surface that has a holding to pre-check (the submit-claim
 * wizard, a group claim's "Add Property" dialog), so the warning, the
 * match-criteria label/value rows, and the three actions stay identical
 * wherever a claim is filed. "Review existing claim" opens the match in a new
 * tab so the in-progress draft is preserved; the primary action acknowledges
 * and proceeds; "Cancel" closes without filing.
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, Layers } from 'lucide-react'

import { StatusBadge } from '@/admin/components/shared/status-badge'
import { type PotentialDuplicateClaim } from '@/admin/hooks/use-claims'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'

export function PotentialDuplicateClaimDialog({
  open,
  onOpenChange,
  matches,
  onContinue,
  isContinuing = false,
  continueLabel,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  matches: PotentialDuplicateClaim[]
  onContinue: () => void
  isContinuing?: boolean
  continueLabel?: string
}) {
  const { t } = useTranslation()

  // Human-readable label for each match criterion (the backend ``matched_on``
  // token that hit), so the reviewer can see exactly what to compare - address,
  // structure, product, installation date (and, for consumer-ref tenants, name
  // / email / phone) - instead of a raw ref key. Free-text ``field:*`` address
  // refs collapse to "address"; anything unmapped falls back to a de-prefixed,
  // humanised key.
  const matchCriterionLabel = useCallback(
    (ref: string): string => {
      if (ref.startsWith('field:')) {
        return t('claims.duplicate_warning.criteria.address', 'address')
      }
      switch (ref) {
        case 'consumer_name':
          return t('claims.duplicate_warning.criteria.name', 'customer name')
        case 'consumer_email':
          return t('claims.duplicate_warning.criteria.email', 'email address')
        case 'consumer_phone':
          return t('claims.duplicate_warning.criteria.phone', 'phone number')
        case 'address':
        case 'consumer_address':
          return t('claims.duplicate_warning.criteria.address', 'address')
        case 'supplier':
          return t('claims.duplicate_warning.criteria.supplier', 'supplier')
        case 'product':
        case 'holding_product':
          return t('claims.duplicate_warning.criteria.product', 'product')
        case 'color':
          return t('claims.duplicate_warning.criteria.color', 'colour')
        case 'holding_structure':
          return t('claims.duplicate_warning.criteria.structure', 'structure')
        case 'installation_date':
          return t(
            'claims.duplicate_warning.criteria.install_date',
            'install date',
          )
        default:
          return ref.replace('consumer_', '').replace(/_/g, ' ')
      }
    },
    [t],
  )

  // WPS-150 (Patrick QA): label for each per-type detail row the backend sends
  // (registration number, structure, installed date, major, product, colour,
  // contractor / supplier contact). Semantic keys, not tenant field names.
  const detailLabel = useCallback(
    (key: string): string => {
      switch (key) {
        case 'registration_number':
          return t(
            'claims.duplicate_warning.detail.registration_number',
            'Registration #',
          )
        case 'structure':
          return t('claims.duplicate_warning.detail.structure', 'Structure')
        case 'installed_date':
          return t(
            'claims.duplicate_warning.detail.installed_date',
            'Installed date',
          )
        case 'major':
          return t('claims.duplicate_warning.detail.major', 'Major defect')
        case 'product':
          return t('claims.duplicate_warning.detail.product', 'Product')
        case 'color':
          return t('claims.duplicate_warning.detail.color', 'Colour')
        case 'contractor_contact':
          return t(
            'claims.duplicate_warning.detail.contractor_contact',
            'Contractor contact',
          )
        case 'contact_name':
          return t('claims.duplicate_warning.detail.contact_name', 'Contact name')
        case 'contact_email':
          return t(
            'claims.duplicate_warning.detail.contact_email',
            'Contact email',
          )
        case 'supplier':
          return t('claims.duplicate_warning.detail.supplier', 'Supplier')
        default:
          return key.replace(/_/g, ' ')
      }
    },
    [t],
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onOpenChange(false)
      }}
    >
      <DialogContent className="max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px] font-normal text-[var(--ink)]">
            <Layers className="h-4 w-4 shrink-0 text-[var(--status-warn)]" />
            {t('claims.duplicate_warning.title', 'Possible duplicate claim')}
          </DialogTitle>
          <DialogDescription className="mt-2 text-[13px] leading-[1.6] text-[var(--ink-2)]">
            {t(
              'claims.duplicate_warning.description',
              'This claim matches one or more existing claims. Review them before continuing, or continue creating this claim.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-1 flex max-h-[280px] flex-col gap-2 overflow-y-auto">
          {matches.map((dup) => (
            <div
              key={dup.claim_uuid}
              className="flex flex-col gap-2 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2.5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] text-[var(--ink)]">
                      {dup.reference}
                    </span>
                    {dup.claim_state ? (
                      <StatusBadge status={dup.claim_state} />
                    ) : null}
                  </div>
                  {dup.consumer_name ? (
                    <span className="truncate text-[12px] text-[var(--ink-2)]">
                      {dup.consumer_name}
                    </span>
                  ) : null}
                </div>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() =>
                    window.open(
                      `/claims/${dup.claim_uuid}`,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                  className="shrink-0 cursor-pointer gap-1 text-[11px]"
                >
                  <ExternalLink className="h-3 w-3" />
                  {t('claims.duplicate_warning.review', 'Review existing claim')}
                </Button>
              </div>
              {(dup.matched_on ?? []).length > 0 ? (
                <div className="flex flex-col gap-1 border-t-[0.5px] border-[var(--rule)] pt-2">
                  <span className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    {t('claims.duplicate_warning.matched_on', 'Matched on')}
                  </span>
                  <div className="flex flex-col gap-1">
                    {(dup.matched_on ?? []).map((ref) => {
                      const value = dup.matched_values?.[ref]
                      return (
                        <div
                          key={ref}
                          className="flex items-baseline justify-between gap-3 text-[11px] leading-[1.5]"
                        >
                          <span className="shrink-0 capitalize text-[var(--ink-3)]">
                            {matchCriterionLabel(ref)}
                          </span>
                          {value ? (
                            <span className="min-w-0 break-words text-right text-[var(--ink)]">
                              {value}
                            </span>
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : null}
              {(() => {
                // WPS-150 (Patrick QA): prefer the backend's ordered, per-type
                // detail rows; fall back to the legacy claim type + defect for
                // tenants still on the ref-based matcher.
                const rows: Array<{ key: string; value: string }> =
                  dup.details && dup.details.length > 0
                    ? dup.details
                    : [
                        ...(dup.claim_type
                          ? [{ key: 'claim_type', value: dup.claim_type }]
                          : []),
                        ...(dup.claim_defect
                          ? [{ key: 'major', value: dup.claim_defect }]
                          : []),
                      ]
                if (rows.length === 0 && dup.is_open === undefined) return null
                return (
                  <div className="flex flex-col gap-1 border-t-[0.5px] border-[var(--rule)] pt-2">
                    <span className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                      {t('claims.duplicate_warning.details', 'Details')}
                    </span>
                    <div className="flex flex-col gap-1">
                      {rows.map((row) => (
                        <div
                          key={row.key}
                          className="flex items-baseline justify-between gap-3 text-[11px] leading-[1.5]"
                        >
                          <span className="shrink-0 text-[var(--ink-3)]">
                            {row.key === 'claim_type'
                              ? t(
                                  'claims.duplicate_warning.claim_type',
                                  'Claim type',
                                )
                              : detailLabel(row.key)}
                          </span>
                          <span className="min-w-0 break-words text-right text-[var(--ink)]">
                            {row.value}
                          </span>
                        </div>
                      ))}
                      {dup.is_open !== undefined ? (
                        <div className="flex items-baseline justify-between gap-3 text-[11px] leading-[1.5]">
                          <span className="shrink-0 text-[var(--ink-3)]">
                            {t('claims.duplicate_warning.status', 'Status')}
                          </span>
                          <span className="min-w-0 break-words text-right text-[var(--ink)]">
                            {dup.is_open
                              ? t('claims.duplicate_warning.open', 'Open')
                              : t('claims.duplicate_warning.closed', 'Closed')}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })()}
            </div>
          ))}
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer px-5 text-[10px] font-normal uppercase tracking-[0.12em]"
          >
            {t('claims.duplicate_warning.cancel', 'Cancel')}
          </Button>
          <Button
            variant="default"
            onClick={onContinue}
            disabled={isContinuing}
            className="cursor-pointer rounded-[6px] bg-[var(--green)] px-5 text-[10px] font-normal uppercase tracking-[0.12em] text-white"
          >
            {continueLabel ??
              t('claims.duplicate_warning.continue', 'Continue creating claim')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
